import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { Table } from "@/components/elements/Table";
import { YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { TGetExamListItemDto } from "@/api/services/admin/learning-content/dtos/get-exam-list.dto";
import { dayjs } from "@/utils/dayjs";
import { EXAM_STATUS_LABEL, EXAM_METHOD_LABEL } from "@/features/learning-content/constants/examOptions";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { Chip, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";

type TExamsTableProps = {
  isLoading: boolean;
  isFetching?: boolean;
  data: TGetExamListItemDto[];
  totalPage?: number;
  industryId: string;
};

export const ExamsTable: FC<TExamsTableProps> = ({ isLoading, isFetching, data, totalPage, industryId }) => {
  const navigate = useNavigate();

  const handleExamClick = (examId: number) => {
    navigate(getDynamicRoute(AppRoutes.learningContentExamEdit, { id: industryId, examId: String(examId) }));
  };

  const columns = useMemo<MRT_ColumnDef<TGetExamListItemDto>[]>(
    () => [
      {
        header: "ステータス",
        accessorKey: "status",
        size: 108,
        Cell: ({ row }) => {
          const statusKey = row.original.status;
          const label = EXAM_STATUS_LABEL[statusKey] ?? statusKey;
          let color: "success" | "default" | "error" = "default";
          if (statusKey === "active") color = "success";
          else if (statusKey === "inactive") color = "error";
          return <Chip label={label} color={color} size="small" />;
        },
        enableSorting: false,
      },
      {
        header: "時期名",
        accessorKey: "periodName",
        size: 141,
        Cell: ({ row }) => {
          const name = row.original.periodName;
          return (
            <Typography
              variant="body14"
              onClick={() => handleExamClick(row.original.id)}
              sx={{ color: "primary.main", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              {name || EMPTY_STR.TEXT}
            </Typography>
          );
        },
      },
      {
        header: "試験日タイプ",
        accessorKey: "examMethod",
        size: 100,
        Cell: ({ row }) => {
          return EXAM_METHOD_LABEL[row.original.examMethod] ?? row.original.examMethod ?? EMPTY_STR.TEXT;
        },
      },
      {
        header: "試験年月",
        accessorKey: "examMonth",
        size: 88,
        Cell: ({ row }) => {
          const val = row.original.examMonth;
          return val ? dayjs(val).format(YEAR_MONTH_FORMAT) : EMPTY_STR.TEXT;
        },
      },
      {
        header: "社内メモ",
        accessorKey: "internalMemo",
        size: 275,
        muiTableHeadCellProps: { sx: { width: "275px", maxWidth: "275px" } },
        muiTableBodyCellProps: { sx: { width: "275px", maxWidth: "275px" } },
        Cell: ({ row }) => {
          return (
            <Typography variant="body14" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {row.original.internalMemo || EMPTY_STR.TEXT}
            </Typography>
          );
        },
      },
      {
        header: "更新日",
        accessorKey: "updatedAt",
        size: 106,
        Cell: ({ row }) => {
          return row.original.updatedAt || row.original.createdAt || EMPTY_STR.TEXT;
        },
      },
      {
        header: "更新者",
        accessorKey: "updatedByName",
        size: 74,
        Cell: ({ row }) => {
          return row.original.updatedByName || row.original.createdByName || EMPTY_STR.TEXT;
        },
      },
    ],
    [industryId],
  );

  const { table } = useTable({
    columns,
    rows: data ?? [],
    enableSorting: false,
    enableRowActions: false,
  });

  return (
    <>
      <Table table={table} isLoading={isLoading} isFetching={isFetching} />
      <NewPagination totalPage={totalPage} />
    </>
  );
};
